import { motion } from "framer-motion";
import { Sparkles, Heart, Calendar, Hand, Gem, MapPin } from "lucide-react";
import { BRAND } from "@/lib/constants";

const TYPES = [
  {
    icon: Sparkles,
    title: "Wedding Invitation Video",
    desc: "Story-driven cinematic invite film with your photos, names, dates and venues — designed to be forwarded.",
  },
  {
    icon: Calendar,
    title: "Save the Date",
    desc: "Short animated reel announcing your date — perfect for Instagram stories and family WhatsApp groups.",
  },
  {
    icon: Hand,
    title: "Mehndi Invite",
    desc: "Bright, playful mehndi function invitation — rangoli, dholki, and your colour palette.",
  },
  {
    icon: Gem,
    title: "Engagement Invite",
    desc: "Elegant ring-ceremony or roka invite — golden serif, hand-drawn ornaments, your story in motion.",
  },
  {
    icon: MapPin,
    title: "Wedding Itinerary",
    desc: "Function-by-function schedule card or animated suite — guests know the dress code, time and venue at a glance.",
  },
  {
    icon: Heart,
    title: "Custom Story Invite",
    desc: "Caricature, illustrated or photographic — your love story rendered as a shareable moment.",
  },
];

export default function InviteTypes() {
  return (
    <section
      id="invite-types"
      data-testid="invite-types-section"
      className="relative py-20 md:py-28 border-t border-white/5"
      style={{
        background: "linear-gradient(180deg, #161210 0%, #1a1414 100%)",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
            <span className="overline text-[var(--iw-gold)]">
              Every Function. Every Style.
            </span>
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
          </div>
          <h2
            className="font-serif-display text-[var(--iw-ivory)] text-[36px] sm:text-5xl md:text-6xl leading-[0.95] tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Which <span className="italic gold-rose-gradient">digital invite</span> do you need?
          </h2>
          <p className="text-[var(--iw-muted)] text-base md:text-lg max-w-xl mx-auto mt-6 font-light">
            Pick a card below or just tell us on WhatsApp — we'll send sample
            reels for that exact function within minutes.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {TYPES.map((t, i) => {
            const Icon = t.icon;
            const msg = `Hi InvitationWala, I want a ${t.title.toLowerCase()}.`;
            return (
              <motion.a
                key={t.title}
                href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(msg)}`}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.7,
                  delay: (i % 3) * 0.08,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="group p-6 md:p-7 transition-colors hover:border-[var(--iw-gold)]/60"
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(212,175,55,0.2)",
                }}
                data-testid={`invite-type-${i}`}
              >
                <Icon
                  size={26}
                  strokeWidth={1.25}
                  className="text-[var(--iw-gold)] mb-4"
                />
                <h3
                  className="font-serif-display text-[var(--iw-ivory)] text-2xl md:text-3xl leading-tight"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {t.title}
                </h3>
                <p className="text-[var(--iw-muted)] text-sm leading-relaxed mt-3">
                  {t.desc}
                </p>
                <span className="overline text-[var(--iw-rose)] mt-5 inline-flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity">
                  Ask on WhatsApp →
                </span>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
